// src/pages/Catalog/components/CategoriesTab.js
import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import openSocket from "../../../services/socket-io";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import CategoryModal from "./CategoryModal";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import { i18n } from "../../../translate/i18n";
import { AuthContext } from "../../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  toolbar: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  searchInput: {
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  addButton: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  tableContainer: {
    flex: 1,
    overflowY: 'auto',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  emptyMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  actions: {
    whiteSpace: 'nowrap',
  },
}));

const CategoriesTab = () => {
  const classes = useStyles();
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const { user } = useContext(AuthContext)

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/categories", {
        params: { searchParam: searchTerm },
      });
      setCategories(data);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [searchTerm]);

  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    const onCategory = (data) => {
      if (data.action === "update" || data.action === "create") {
        fetchCategories();
      }

      if (data.action === "delete") {
        setCategories(prev => prev.filter(c => c.id !== parseInt(data.categoryId, 10)));
      }
    };
    socket.on("category", onCategory);

    return () => {
      socket.off("category", onCategory);
    };
  }, []);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleOpenModal = (category = null) => {
    setSelectedCategory(category);
    setModalOpen(true);
  };

  const handleSaveCategory = async (categoryData) => {
    await fetchCategories();
    setModalOpen(false);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setConfirmModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/categories/${categoryToDelete.id}`);
      toast.success("Categoría eliminada con éxito");
      setCategories(categories.filter((c) => c.id !== categoryToDelete.id));
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setCategoryToDelete(null);
  };

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <div className={classes.searchContainer}>
          <TextField
            className={classes.searchInput}
            placeholder="Buscar categorías..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />

          {user?.profile === "admin" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              className={classes.addButton}
            >
              {i18n.t("categories.buttons.add", "Añadir categoría")}
            </Button>
          )}

        </div>
      </div>

      <Paper className={classes.tableContainer} variant="outlined">
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : categories.length === 0 ? (
          <div className={classes.emptyMessage}>
            <Typography variant="body1" color="textSecondary">
              No se encontraron categorías
            </Typography>
          </div>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Descripción</TableCell>
                  {user?.profile === "admin" && (
                    <TableCell align="right">Acciones</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.id}</TableCell>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>
                      {category.description
                        ? category.description.length > 50
                          ? `${category.description.slice(0, 50)}...`
                          : category.description
                        : ""}
                    </TableCell>
                    
                    {user?.profile === "admin" && (
                    <TableCell align="right" className={classes.actions}>
                      <IconButton
                        size="small"
                        title="Editar"
                        onClick={() => handleOpenModal(category)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Eliminar"
                        onClick={() => handleDeleteClick(category)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    )}


                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <CategoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        categoryId={selectedCategory?.id}
        onSave={handleSaveCategory}
      />

      <DeleteConfirmationModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Categoría"
        message={`¿Estás seguro de que deseas eliminar la categoría "${categoryToDelete?.name}"? Esta acción no se puede deshacer.`}
      />
    </div>
  );
};

export default CategoriesTab;